import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import omnicareLogo from "../assets/icons/logo.svg";
import backIcon from "../assets/icons/back.svg";
import LanguageSwitcher from "./LanguageSwitcher";
import "../styles/components/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="privacy-page">
      <header className="privacy-header">
        <div className="logo-section">
          <div className="logo" onClick={() => navigate("/landing")} style={{ cursor: "pointer" }}>
            <img src={omnicareLogo} alt="Pebble Health Logo" className="logo-icon" />
            <span className="logo-text">Pebble Health</span>
          </div>
        </div>
        <div className="header-actions">
          <LanguageSwitcher />
          <div className="back-button" onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
            <img src={backIcon} alt="Back" className="back-icon" />
          </div>
        </div>
      </header>

      <main className="privacy-content">
        <h1>{t('privacy.title', 'Privacy Policy')}</h1>
        <p className="privacy-updated">{t('privacy.lastUpdated', 'Last updated: February 2026')}</p>

        <section>
          <h2>{t('privacy.tldr.title', 'The Short Version')}</h2>
          <p>{t('privacy.tldr.body', 'We built Pebble Health to help you understand your cancer screening needs. We take your privacy seriously — your health information stays on your device, we don\'t sell your data, and any email you share with us is completely optional. That\'s it.')}</p>
        </section>

        <section>
          <h2>{t('privacy.whatWeCollect.title', 'What We Collect')}</h2>

          <h3>{t('privacy.whatWeCollect.quiz.title', 'Your Quiz Responses')}</h3>
          <p>{t('privacy.whatWeCollect.quiz.body', 'When you take our health assessment, your answers (age, sex, lifestyle habits, family history) are processed locally in your browser to generate your personalized screening recommendations. We do not send your individual quiz responses to our servers.')}</p>

          <h3>{t('privacy.whatWeCollect.email.title', 'Email Address (Optional)')}</h3>
          <p>{t('privacy.whatWeCollect.email.body', 'If you choose to share your email, we\'ll only use it to send you screening reminders and health tips. You can unsubscribe at any time. No email, no problem — the tool works exactly the same without it.')}</p>

          <h3>{t('privacy.whatWeCollect.analytics.title', 'Anonymous Usage Data')}</h3>
          <p>{t('privacy.whatWeCollect.analytics.body', 'We collect basic, anonymous analytics (like page views and which quiz sections people complete) to improve the tool. This data cannot identify you personally. We don\'t use cookies for tracking.')}</p>
        </section>

        <section>
          <h2>{t('privacy.whatWeDont.title', 'What We Don\'t Do')}</h2>
          <ul>
            <li>{t('privacy.whatWeDont.sell', 'We don\'t sell your data. Ever.')}</li>
            <li>{t('privacy.whatWeDont.share', 'We don\'t share your health information with insurers, employers, or advertisers.')}</li>
            <li>{t('privacy.whatWeDont.store', 'We don\'t store your quiz responses on our servers — they stay in your browser.')}</li>
            <li>{t('privacy.whatWeDont.track', 'We don\'t use tracking cookies or fingerprinting.')}</li>
          </ul>
        </section>

        <section>
          <h2>{t('privacy.dataStorage.title', 'Where Your Data Lives')}</h2>
          <p>{t('privacy.dataStorage.body', 'Your quiz responses and screening results are stored locally in your browser (localStorage). This means they\'re only on your device. If you clear your browser data, your results are gone — we can\'t recover them because we never had them.')}</p>
        </section>

        <section>
          <h2>{t('privacy.guidelines.title', 'Our Screening Recommendations')}</h2>
          <p>{t('privacy.guidelines.body', 'Our recommendations are based on published clinical guidelines from the U.S. Preventive Services Task Force (USPSTF), American Cancer Society (ACS), and National Comprehensive Cancer Network (NCCN). We review and update our guidelines database regularly. This tool is for informational purposes only and does not replace professional medical advice — please consult your doctor for personalized screening decisions.')}</p>
        </section>

        <section>
          <h2>{t('privacy.rights.title', 'Your Rights')}</h2>
          <p>{t('privacy.rights.body', 'You can delete your local data at any time by clearing your browser storage. If you\'ve shared your email with us and want it removed, just email us and we\'ll take care of it within 48 hours.')}</p>
        </section>

        <section>
          <h2>{t('privacy.california.title', 'For California Residents')}</h2>
          <p>{t('privacy.california.body', 'Under the California Consumer Privacy Act (CCPA), you have the right to know what data we collect, request deletion, and opt out of data sales. Since we don\'t sell data and store minimal information, exercising these rights is straightforward — just reach out.')}</p>
        </section>

        <section>
          <h2>{t('privacy.children.title', 'Children\'s Privacy')}</h2>
          <p>{t('privacy.children.body', 'Pebble Health is designed for adults. We do not knowingly collect information from anyone under 18. Our screening recommendations are based on adult clinical guidelines.')}</p>
        </section>

        <section>
          <h2>{t('privacy.changes.title', 'Changes to This Policy')}</h2>
          <p>{t('privacy.changes.body', 'If we update this policy, we\'ll post the changes here with a new date. No surprises.')}</p>
        </section>

        <section>
          <h2>{t('privacy.contact.title', 'Questions?')}</h2>
          <p>{t('privacy.contact.body', 'We\'re real people and happy to answer any questions about your privacy.')}</p>
          <p className="privacy-email">
            📧 <a href="mailto:omnicareai@gmail.com">omnicareai@gmail.com</a>
          </p>
        </section>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
